import { useEffect, useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";

/**
 * Bottom-center notice shown when several photos fail to load (storage
 * hiccup). Tiles retry on their own; this just tells the client what's
 * happening and offers a manual retry. Hides itself once a retried photo
 * loads again.
 */
export default function StorageStatusBanner() {
  const [degraded, setDegraded] = useState(false);

  useEffect(() => {
    let errors = 0;
    let reset = null;
    const onError = () => {
      errors += 1;
      clearTimeout(reset);
      reset = setTimeout(() => { errors = 0; }, 30000);
      if (errors >= 2) setDegraded(true);
    };
    const onOk = () => { errors = 0; setDegraded(false); };
    window.addEventListener("lumetier:photo-error", onError);
    window.addEventListener("lumetier:photo-ok", onOk);
    return () => {
      clearTimeout(reset);
      window.removeEventListener("lumetier:photo-error", onError);
      window.removeEventListener("lumetier:photo-ok", onOk);
    };
  }, []);

  if (!degraded) return null;
  return (
    <div
      role="status"
      className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[60] w-[calc(100%-2rem)] max-w-xl sm:w-auto flex items-center gap-3 px-4 py-2.5 bg-stone-900/92 text-stone-100 backdrop-blur-md shadow-2xl border border-white/10"
      data-testid="storage-status-banner"
    >
      <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-300 shrink-0" />
      <span className="text-xs tracking-wide flex-1 min-w-0">
        Photos are reconnecting<span className="hidden sm:inline"> — some images may take a moment to appear</span>.
      </span>
      <button
        onClick={() => window.dispatchEvent(new Event("lumetier:photo-retry"))}
        className="inline-flex items-center gap-1 whitespace-nowrap text-[10px] uppercase tracking-[0.2em] text-amber-300 hover:text-white transition-colors"
        data-testid="storage-status-retry"
      >
        <RefreshCw className="w-3 h-3" /> Retry now
      </button>
    </div>
  );
}
